import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import "./CustomSelect.css";

/**
 * Dropdown accesible (patrón combobox/listbox) que reemplaza al <select>
 * nativo para poder controlar por completo su apariencia y animaciones.
 */
const CustomSelect = ({
  name,
  label,
  icon: Icon,
  options,
  value,
  onChange,
  placeholder = "Selecciona una opción",
  disabled = false,
  required = false,
  isInvalid = false,
  emptyMessage = "No hay opciones disponibles",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const controlId = `select-${useId()}`;
  const listboxId = `${controlId}-listbox`;

  const selectedOption = options.find(
    (opt) => String(opt.value) === String(value)
  );

  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  };

  // El menú se porta a document.body (ver render) para escapar del stacking
  // context que crean las animaciones (transform) de las filas del formulario;
  // por eso su posición se calcula en coordenadas de viewport (position: fixed).
  useLayoutEffect(() => {
    if (isOpen) updateMenuPosition();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onClickOutside = (event) => {
      const clickedTrigger = rootRef.current && rootRef.current.contains(event.target);
      const clickedMenu = listRef.current && listRef.current.contains(event.target);
      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    const onReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const idx = options.findIndex((opt) => String(opt.value) === String(value));
    setHighlightedIndex(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const activeEl = listRef.current.querySelector('[data-active="true"]');
      if (activeEl) activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [isOpen, highlightedIndex]);

  const commitSelection = (option) => {
    onChange(name, option.value);
    setIsOpen(false);
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (options.length > 0) {
          const dir = event.key === "ArrowDown" ? 1 : -1;
          setHighlightedIndex((prev) => {
            const base = prev < 0 ? 0 : prev;
            return (base + dir + options.length) % options.length;
          });
        }
        break;
      }
      case "Enter":
      case " ":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (options[highlightedIndex]) {
          commitSelection(options[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      case "Home":
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case "End":
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(options.length - 1);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`custom-select${isOpen ? " is-open" : ""}${
        disabled ? " is-disabled" : ""
      }${isInvalid ? " is-invalid" : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        id={controlId}
        ref={triggerRef}
        className="custom-select-trigger"
        onClick={() => !disabled && setIsOpen((open) => !open)}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-required={required}
        aria-label={label}
      >
        {Icon && <Icon className="custom-select-icon" aria-hidden="true" />}
        <span
          className={`custom-select-value${
            selectedOption ? "" : " is-placeholder"
          }`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FiChevronDown className="custom-select-chevron" aria-hidden="true" />
      </button>

      {createPortal(
        <ul
          id={listboxId}
          role="listbox"
          ref={listRef}
          className={`custom-select-menu${isOpen ? " is-open" : ""}`}
          aria-label={label}
          style={{
            top: menuPosition?.top ?? -9999,
            left: menuPosition?.left ?? -9999,
            width: menuPosition?.width ?? 0,
          }}
        >
          {options.length === 0 && (
            <li className="custom-select-empty">{emptyMessage}</li>
          )}
          {options.map((option, index) => {
            const isSelected = String(option.value) === String(value);
            const isHighlighted = index === highlightedIndex;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                data-active={isHighlighted}
                className={`custom-select-option${
                  isSelected ? " is-selected" : ""
                }${isHighlighted ? " is-highlighted" : ""}`}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => commitSelection(option)}
              >
                <span>{option.label}</span>
                {isSelected && (
                  <FiCheck className="custom-select-check" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default CustomSelect;
